import React, { useState } from 'react';

import { FmtpEventType, Permissions, v2_parcelSearchEvent } from '@lanterds/types';
import DetailSection from 'src/components/common/DetailSection/DetailSection';
import DataTable from 'src/components/common/DataTable/DataTable';
import { formatAndHandleUtcFallback } from 'src/util/formatters';
import { Indexable } from 'src/interfaces/interfaces';
import { Box, Dialog, DialogContent, Tooltip } from '@mui/material';
import MapIcon from '@mui/icons-material/Map';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import { isAuthorized } from 'src/util/Permissions';
import { getUserEmailFromToken } from 'src/util/helpers';
import './ScanHistory.scss';
import { DeliveryImageTooltip } from './DeliveryImageTooltip';

interface ScanHistoryProps {
    data: v2_parcelSearchEvent[];
}

export default function ScanHistory({ data }: ScanHistoryProps) {
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);

    const handleOpenModal = (image: string) => {
        setSelectedImage(image);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setSelectedImage(null);
        setModalOpen(false);
    };

    const tipMessage = 'All times are local unless otherwise specified';
    const labels = [
        <>
            Scan Date{' '}
            <Tooltip arrow title={tipMessage} className="info-tooltip">
                <InfoTwoToneIcon color="primary" />
            </Tooltip>
        </>,
        'Scan Type',
        'Type',
        'Name',
        'Address',
        'City',
        'State',
        'Damage',
        <>
            Date Sent At{' '}
            <Tooltip arrow title={tipMessage} className="info-tooltip">
                <InfoTwoToneIcon color="primary" />
            </Tooltip>
        </>,
        'Scanned By',
        '',
    ];
    const keys = [
        'scanDate',
        'scanType',
        'containerType',
        'locationName',
        'addressStreet1',
        'addressCity',
        'addressState',
        'damage',
        'dateSentAt',
        'scannedBy',
        'mapLink',
    ];

    const userEmail = getUserEmailFromToken();
    const hasAccessToPicutres = isAuthorized(userEmail, Permissions.parcelSearch_deliveryPictures);
    const hasAccessToAllPictures = isAuthorized(userEmail, Permissions.parcelSearch_deliveryPicturesIncludeAll);

    if (hasAccessToPicutres) {
        labels.splice(11, 0, '');
        keys.splice(11, 0, 'deliveryPicture');
    }

    const getGoogleMapUrl = (latitude: string, longitude: string) => {
        return `https://maps.google.com?q=${latitude},${longitude}`;
    };

    const createDateSentAtTooltip = (source: v2_parcelSearchEvent['identifier']) => {
        if (!source) return '';
        return source.map((source) => {
            return (
                <Box className="scan-history-source">
                    <Box className="tooltip-item-label">{'Source:'}</Box>
                    <Box className="tooltip-item-value">{source.sourceSystemMemo ?? source.sourceSystem}</Box>
                </Box>
            );
        });
    };

    let transformedData: Array<Indexable> = [];

    if (data && data?.length > 0) {
        transformedData = data
            .filter((event: v2_parcelSearchEvent) => event.eventType !== FmtpEventType.ASN)
            .map((datum) => {
                const mapUrl = datum?.latitude && datum.longitude ? getGoogleMapUrl(datum.latitude, datum.longitude) : '';
                return {
                    scanDate: formatAndHandleUtcFallback(datum.scanTimeLocal, datum.scanTimeUtc),
                    scanType: datum?.eventType + (datum.scanSubType ? ` - ${datum.scanSubType}` : ''),
                    scannedAt: datum?.scannedAt,
                    scannedBy: datum?.scannedBy,
                    // damage: // TODO: Add when BE ready
                    dateSentAt: (
                        <Tooltip arrow title={createDateSentAtTooltip(datum.identifier)} placement="right">
                            {<span>{formatAndHandleUtcFallback(datum.dateSentAtLocal, datum.dateSentAtUtc, 'UNKNOWN')}</span>}
                        </Tooltip>
                    ),
                    containerType: datum?.containerType,
                    locationNumber: datum?.currentLocationCode,
                    locationName: datum?.currentStopName,
                    addressStreet1: datum?.currentAddressLineOne,
                    addressCity: datum?.currentCity,
                    addressState: datum?.currentState,
                    mapLink: mapUrl ? (
                        <a target="_blank" rel="noreferrer" href={mapUrl}>
                            <MapIcon color="primary" style={{ cursor: 'pointer' }} />
                        </a>
                    ) : (
                        ''
                    ),
                    deliveryPicture: datum.scanImage ? (
                        <DeliveryImageTooltip
                            imageBase64String={datum.scanImage}
                            scanImageStatus={datum.scanImageStatus}
                            handleOnClick={handleOpenModal}
                            hasAccessToAllPictures={hasAccessToAllPictures}
                        />
                    ) : null,
                };
            });
    }

    const content =
        transformedData && transformedData?.length ? <DataTable labels={labels} keys={keys} data={transformedData} /> : <></>;

    return (
        <>
            <DetailSection id="scan-history" title="Scan History">
                {content}
            </DetailSection>

            <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md">
                <DialogContent className="modal-image-container">
                    {selectedImage && (
                        <img src={`data:image/jpeg;base64,${selectedImage}`} alt="Delivery" className="modal-image" />
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}

ScanHistory.defaultProps = {
    data: [],
};
